import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Star, ShoppingBag, Award } from "lucide-react";
import { api } from "@/lib/api";
import { HotProductBadge } from "@/components/landing/HotProductBadge";
import AnnouncementBar from "@/components/landing/AnnouncementBar";
import Header from "@/components/landing/Header";
import ContactSection from "@/components/landing/ContactSection";
import Footer from "@/components/landing/Footer";
import { useSeo, SEO_KEYWORDS } from "@/hooks/useSeo";

export default function CatalogPage() {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/products")
            .then((r) => setProducts(r.data))
            .catch(() => setProducts([]))
            .finally(() => setLoading(false));
    }, []);

    useSeo({
        title: "Shop Hair Growth Serum & Scalp Massager Comb in Pakistan | Resham Drops",
        description: "Shop PCSIR certified rosemary hair growth serum and crystal scalp massager comb. Hair fall treatment products with cash on delivery across Pakistan.",
        keywords: SEO_KEYWORDS,
        path: "/shop",
        jsonLd: products.length
            ? {
                  "@context": "https://schema.org",
                  "@type": "ItemList",
                  itemListElement: products.map((p, i) => ({
                      "@type": "ListItem",
                      position: i + 1,
                      url: `${window.location.origin}/product/${p.slug}`,
                      name: p.name,
                  })),
              }
            : null,
    });

    const flagship = products.find((p) => p.is_flagship) || products[0];
    const rest = products.filter((p) => p && p !== flagship);

    return (
        <div
            data-testid="catalog-page"
            className="min-h-screen bg-cream text-forest-900 font-sans"
        >
            <AnnouncementBar />
            <Header />

            {/* Hero banner */}
            <section className="relative py-14 sm:py-20 overflow-hidden">
                <div className="absolute inset-0 pointer-events-none">
                    <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-forest-100/60 blur-3xl" />
                    <div className="absolute -bottom-24 -left-24 w-80 h-80 rounded-full bg-gold-300/30 blur-3xl" />
                </div>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
                    <span className="inline-flex items-center gap-2 bg-forest-50 border border-forest-100 text-forest-800 text-xs font-bold px-3 py-1.5 rounded-full uppercase tracking-widest">
                        <Sparkles className="w-3.5 h-3.5 text-gold-600" />
                        Botanical Hair Science · Pakistan
                    </span>
                    <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-bold text-forest-800 leading-[1.03] mt-5 max-w-4xl mx-auto">
                        Honestly natural care,
                        <br />
                        <span className="italic">deeply</span> loved by Pakistan.
                    </h1>
                    <p className="text-forest-900/70 text-base sm:text-lg mt-5 max-w-2xl mx-auto">
                        Every drop is PCSIR-certified, dermatologist-approved
                        and shipped free across Pakistan with cash on delivery.
                    </p>
                </div>
            </section>

            {/* Flagship */}
            {flagship && (
                <section className="pb-12 sm:pb-16">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="bg-forest-900 rounded-3xl p-6 sm:p-10 lg:p-14 text-cream grid grid-cols-1 lg:grid-cols-12 gap-10 items-center shadow-2xl relative overflow-hidden">
                            <div className="absolute -right-16 -top-16 w-80 h-80 rounded-full bg-gold-500/10 blur-3xl pointer-events-none" />
                            <div className="lg:col-span-6 space-y-4 relative z-10">
                                <span className="inline-flex items-center gap-1.5 bg-gold-500/15 text-gold-400 border border-gold-500/30 text-[11px] font-bold px-3 py-1 rounded-full uppercase tracking-widest">
                                    <Award className="w-3.5 h-3.5" /> Flagship
                                </span>
                                <h2 className="font-serif text-4xl sm:text-5xl font-bold leading-tight">
                                    {flagship.name}
                                </h2>
                                <p className="text-cream/80 text-base leading-relaxed max-w-md">
                                    {flagship.short_description}
                                </p>
                                <div className="flex items-center gap-3">
                                    <div className="flex text-gold-400">
                                        {[...Array(5)].map((_, i) => (
                                            <Star
                                                key={i}
                                                className="w-4 h-4 fill-gold-400"
                                            />
                                        ))}
                                    </div>
                                    <span className="text-xs text-cream/70 font-semibold">
                                        1,482+ verified reviews
                                    </span>
                                </div>
                                <div className="flex items-baseline gap-3 pt-2">
                                    <span className="font-serif text-3xl sm:text-4xl font-bold text-gold-400">
                                        {flagship.bundles[0]?.price.toLocaleString()} PKR
                                    </span>
                                    <span className="text-sm text-cream/40 line-through">
                                        {flagship.bundles[0]?.original_price.toLocaleString()} PKR
                                    </span>
                                </div>
                                <Link
                                    to={`/product/${flagship.slug}`}
                                    data-testid="flagship-cta"
                                    className="inline-flex items-center gap-2 bg-gold-500 hover:bg-gold-600 text-forest-900 font-bold text-sm sm:text-base px-6 py-3.5 rounded-full shadow-xl transition-all"
                                >
                                    <ShoppingBag className="w-4 h-4" />
                                    Shop the Flagship
                                </Link>
                            </div>
                            <div className="lg:col-span-6 relative z-10">
                                <div className="aspect-square rounded-3xl overflow-hidden bg-forest-800 border border-gold-500/20 shadow-2xl">
                                    <img
                                        src={flagship.images?.[0]}
                                        alt={flagship.name}
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* Catalog grid */}
            <section id="shop" className="py-10 sm:py-14 bg-white border-y border-forest-100">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-end justify-between mb-8 sm:mb-10">
                        <div>
                            <span className="text-gold-600 font-bold text-xs uppercase tracking-[0.25em] block mb-2">
                                Full Catalog
                            </span>
                            <h2 className="font-serif text-3xl sm:text-5xl font-bold text-forest-800 leading-tight">
                                Shop every Resham Drop.
                            </h2>
                        </div>
                        <span className="hidden sm:block text-xs font-semibold text-forest-700/70">
                            {products.length}{" "}
                            {products.length === 1 ? "product" : "products"}
                        </span>
                    </div>

                    {loading && (
                        <div
                            data-testid="catalog-loading"
                            className="text-sm text-forest-700/60 py-16 text-center"
                        >
                            Loading products…
                        </div>
                    )}

                    {!loading && products.length === 0 && (
                        <div className="text-center py-16 text-forest-700/60 text-sm">
                            No products available yet.
                        </div>
                    )}

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {products.map((p) => (
                            <Link
                                to={`/product/${p.slug}`}
                                key={p.id}
                                data-testid={`product-card-${p.slug}`}
                                className="group bg-cream rounded-3xl border border-forest-100 overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                            >
                                <div className="aspect-square overflow-hidden bg-forest-50 relative">
                                    {p.is_flagship && (
                                        <span className="absolute top-3 left-3 z-10 bg-forest-800 text-gold-400 text-[10px] font-bold px-2 py-1 rounded-full uppercase tracking-widest border border-gold-500/40">
                                            Flagship
                                        </span>
                                    )}
                                    <span className="absolute top-3 right-3 z-10">
                                        <HotProductBadge productId={p.id} testid={`hot-badge-${p.slug}`} />
                                    </span>
                                    <img
                                        src={p.images?.[0]}
                                        alt={p.name}
                                        onError={(e) => {
                                            e.currentTarget.src =
                                                "https://placehold.co/600x600/163721/e4c267?text=Resham+Drops";
                                        }}
                                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                    />
                                </div>
                                <div className="p-5 space-y-2">
                                    <span className="text-[10px] font-bold uppercase tracking-widest text-gold-600">
                                        {p.category || "Hair Care"}
                                    </span>
                                    <h3 className="font-serif font-bold text-forest-800 text-lg leading-tight line-clamp-2">
                                        {p.name}
                                    </h3>
                                    <p className="text-xs text-forest-900/70 line-clamp-2">
                                        {p.short_description}
                                    </p>
                                    <div className="flex items-baseline justify-between pt-2 border-t border-forest-100">
                                        <div>
                                            <span className="font-serif text-lg font-bold text-forest-800">
                                                {p.bundles?.[0]?.price?.toLocaleString() || "—"} PKR
                                            </span>
                                            {p.bundles?.[0]?.original_price > p.bundles?.[0]?.price && (
                                                <span className="text-xs text-forest-900/40 line-through ml-2">
                                                    {p.bundles[0].original_price.toLocaleString()} PKR
                                                </span>
                                            )}
                                        </div>
                                        <span className="text-xs font-bold text-forest-700 group-hover:text-gold-600 transition-colors">
                                            View →
                                        </span>
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            <ContactSection />
            <Footer />
        </div>
    );
}
